/**
 * Write or edit a plugin by hand: manifest (JSON) and code. Checked before it
 * is saved: the manifest must be valid, the code must run in the sandbox, and
 * every block, command, view and automation in the manifest must exist in it.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { readManifest, savePlugin, type InstalledPlugin } from '@nexus/plugins';
import { Dialog } from '../components/Dialog';
import { useStore } from '../db/context';
import { unloadPlugin } from './runtime';
import { checkPlugin, usePluginEditor } from './editorState';

const EXAMPLE_MANIFEST = {
  id: 'open-taken',
  name: 'Open taken per project',
  version: '1.0.0',
  description: 'Telt de open taken per project.',
  permissions: { read: true, write: false, network: [] },
  blocks: [{ id: 'per-project', title: 'Open taken per project' }],
};

const EXAMPLE_CODE = `nexus.block('per-project', async () => {
  const tasks = await nexus.tasks.list({ status: 'open' });
  const per = {};
  for (const t of tasks) per[t.project || 'Zonder project'] = (per[t.project || 'Zonder project'] || 0) + 1;
  return {
    type: 'bars',
    items: Object.entries(per).map(([label, value]) => ({ label, value })),
  };
});
`;

export function PluginEditor() {
  const { t } = useTranslation();
  const editing = usePluginEditor((s) => s.editing);
  const set = usePluginEditor((s) => s.set);
  return (
    <Dialog
      open={!!editing}
      onOpenChange={(o) => !o && set(null)}
      title={editing === 'new' ? t('plugins.editor.newTitle') : t('plugins.editor.editTitle')}
      wide
    >
      {editing && (
        <Body
          key={editing === 'new' ? 'new' : editing.pageId}
          plugin={editing}
          onDone={() => set(null)}
        />
      )}
    </Dialog>
  );
}

function Body({ plugin, onDone }: { plugin: InstalledPlugin | 'new'; onDone: () => void }) {
  const { t } = useTranslation();
  const store = useStore();
  const [manifestText, setManifestText] = useState(
    JSON.stringify(plugin === 'new' ? EXAMPLE_MANIFEST : plugin.manifest, null, 2),
  );
  const [code, setCode] = useState(plugin === 'new' ? EXAMPLE_CODE : plugin.code);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const area = 'min-h-48 w-full rounded-lg border border-border bg-bg p-2 font-mono text-xs';

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      let raw: unknown;
      try {
        raw = JSON.parse(manifestText);
      } catch {
        return setError(t('plugins.editor.badJson'));
      }
      const m = readManifest(raw);
      if ('error' in m) return setError(m.error);
      const problem = await checkPlugin(m.manifest, code);
      if (problem) return setError(problem);
      const saved = await savePlugin(store, {
        manifest: m.manifest,
        code,
        source: plugin === 'new' ? 'manual' : plugin.source,
      });
      unloadPlugin(saved.pageId);
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 text-sm">
      <p className="text-xs text-fg-muted">{t('plugins.editor.intro')}</p>
      <label className="flex flex-col gap-1">
        {t('plugins.editor.manifest')}
        <textarea
          spellCheck={false}
          value={manifestText}
          onChange={(e) => setManifestText(e.target.value)}
          className={area}
        />
      </label>
      <label className="flex flex-col gap-1">
        {t('plugins.editor.code')}
        <textarea
          spellCheck={false}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className={`${area} min-h-72`}
        />
      </label>
      {error && (
        <p role="alert" className="text-danger">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onDone} className="rounded-md px-3 py-1.5 hover:bg-bg-hover">
          {t('plugins.cancel')}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void save()}
          className="rounded-full bg-accent px-3 py-1.5 font-medium text-accent-fg disabled:opacity-50"
        >
          {t('plugins.editor.save')}
        </button>
      </div>
    </div>
  );
}
